import React from "react";
import Header from "../../case-study/text-layouts/Header";
import SubHeader from "../../case-study/text-layouts/SubHeader";
import GridThree from "../../case-study/image-layouts/GridThree";
import Single from "../../case-study/image-layouts/Single";
import ImageText from "../../case-study/image-layouts/ImageText";
import NavBar from "../../common/NavBar";

const sectionSpacer: React.CSSProperties = { height: 100 };
const innerSpacer: React.CSSProperties = { height: 64 };

const NineFin = () => {
  return (
    <>
      <NavBar />
      <Header
        heading="Building, not Coding"
        subHeading="I look beyond feature delivery; finding opportunities where I can evidence the impact-to-effort and support the company OKRs."
        role="Senior Software Engineer"
        tags="Product Engineering, Fintech, Distressed Debt"
      />

      <SubHeader
        heading="Notifications, the why not the how"
        subHeading="This project came about after an internal release of an access request form. Our users dogfooded it, and combined with previous customer qual feedback about emails ('how do I switch these off!'), product prioritised preferences on notifications."
      />
      <div style={innerSpacer} aria-hidden="true" />
      <GridThree
        imageOne="/images/9fin-notif-01.svg"
        captionOne="(1a) The proposed design: a user can switch notifications on and off for team access changes to private resources. The solution touched 4 separate services, affected 3 different user types and stacked multiple layers of conditional logic."
        imageTwo="/images/9fin-notif-02.svg"
        captionTwo="(1b) Understanding how our product is used today: our permissions service for teams was designed for orgs to create teams that can see different issuers' private resources. In reality, orgs were throwing every user into one team. So even with the toggle, notifications are still scoped per team, meaning users would receive every org-wide change anyway. (Note: product aren't engaging with Amplitude, more on this later.)"
        imageThree="/images/9fin-notif-03.svg"
        captionThree="(1c) What users actually care about: not whether their team got access, but what they personally have gained access to."
      />

      <div style={sectionSpacer} aria-hidden="true" />

      <SubHeader
        heading="Restoring trust in the Analytics layer"
        subHeading="Every product, growth and retention decision sits on top of usage metrics; I was able to increase 10% of weekly visits, and rebuilt confidence in the metrics teams were shipping against."
      />
      <div style={innerSpacer} aria-hidden="true" />
      <Single
        layout="start"
        image="/images/9fin-proxy-00.svg"
        mobile="/images/9fin-proxy-00.svg"
        caption="(2a) The data needed scrutiny: a year of tracking inaccuracies. I started by auditing events against our backend logs to understand what the current state was."
      />
      <div style={innerSpacer} aria-hidden="true" />
      <GridThree
        imageOne="/images/9fin-proxy-01.svg"
        captionOne="(2b) The signal: backend logs were recording sessions Amplitude never saw, a steady ~10% undercount distorting every metric downstream."
        imageTwo="/images/9fin-proxy-02.svg"
        captionTwo="(2c) The cause: third-party Amplitude requests were being intercepted by ad blockers in the browser, surfaced after I asked an affected internal user to share their session."
        imageThree="/images/9fin-proxy-03.svg"
        captionThree="(2d) The fix: a first-party proxy on our own domain forwards events server-side, lifting reported weekly visits 4 to 10%."
      />
      <div style={sectionSpacer} aria-hidden="true" />

      <SubHeader
        heading="Prototyping simplified"
        subHeading="I built 9Proto, a hackathon-winning project enabling anyone to create and share their ideas with two commands: make proto, make ship."
      />
      <ImageText
        image="/images/9fin-proto-01.svg"
        mobile="/images/9fin-proto-01.svg"
        alt="proto-light: frontend-only mode with MSW mocks"
        title="Local development is too complex to just build a flow"
        problem="Standing up the full 9fin app locally needs the API key, a database, feature flags, auth and seed data. Too much friction for 'I want to try one flow with someone', so most people give up and ship a Figma instead."
        solution="proto-light: a frontend-only mode where MSW mocks every API call, auth is bypassed and feature flags are pinned to production values. One make proto stands up the whole prototype."
      />
      <ImageText
        image="/images/9fin-proto-02.svg"
        mobile="/images/9fin-proto-02.svg"
        alt="shareable deploy: public URL per branch"
        title="Get the 'real thing' in front of people, fast"
        problem="Figma artefacts don't read as the real product. Stakeholders interact with them as drawings, not software, so feedback never converges on what we'd actually ship."
        solution="Shareable deploy: every prototype branch auto-builds to a static site with a public URL. No VPN, no SSO, no backend, just a link a stakeholder or client can open. make ship does it."
      />
      <ImageText
        image="/images/9fin-proto-03.svg"
        mobile="/images/9fin-proto-03.svg"
        alt="proto-pro: full stack locally with MSW fallback on deploy"
        title="When you need real data and real backend behaviour, you should have it"
        problem="Some prototypes need real APIs, real auth state and cross-page session data. MSW mocks fall apart when the question is 'does this hold up under real backend behaviour?'"
        solution="proto-pro: full stack locally for prototypes that need it, while the deployed build still falls back to MSW so the public link never exposes real data."
      />
      <Single
        layout="start"
        image="/images/9fin-proto-04.svg"
        mobile="/images/9fin-proto-04.svg"
        caption="Under the hood: the main app is pulled in as a git submodule pinned to a frozen SHA, with a five-file Vite resolver swap layering proto-only overrides on top. Mocks are MSW with three handler tiers, and the world is generated deterministically at boot from faker.seed(7). CI ships the static bundle to S3 and CloudFront gates the public URL with a single KV read for basic-auth, no backend, no cold start."
      />
      <div style={{ height: 160 }} aria-hidden="true" />
    </>
  );
};

export default NineFin;
